// PRD 05 §3.7 — slide-panel form for creating / editing a question.
//
// Layout (top to bottom):
//   1. 대상 (radio)
//   2. 성별 (radio)
//   3. 적용 범위 (select) + conditional 질병군 / 질병코드 (select)
//   4. 질문 원문 (textarea)
//   5. 답변유형 (select)
//   6. Dynamic answer-area editor per type
//   7. 📷 사진첨부 허용 (checkbox)
//   8. 점수 산출 설정 (toggle + 4 inputs + 2 derived + formula)
//   9. 취소 / 저장
//
// The save button stays disabled until the required-field check passes
// (PRD §3.7). On click, the broader §8.x check runs and surfaces any remaining
// error via toast.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { useDeployDirtyStore } from '@/features/pro-deployment/store/deploy-dirty.store';

import { PostSaveDeployNotice } from './PostSaveDeployNotice';
import { useDiseaseFilterOptionsQuery } from '../hooks/use-disease-filter-options';
import { useInheritedItemScope } from '../hooks/use-item-scope';
import { useItemQuestionsQuery } from '../hooks/use-item-questions';
import {
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
} from '../hooks/use-question-mutations';
import { useQuestionnaireDesignPageStore } from '../store/questionnaire-design-page.store';
import type {
  AnswerType,
  Applicability,
  Gender,
  Question,
  ScoringAdvanced,
  Target,
} from '../types/questionnaire-design.types';
import {
  collectFieldErrors,
  draftToUpdateInput,
  makeScoreOutOfBand,
  type QuestionDraft,
  type QuestionFieldErrors,
} from '../utils/question-validation';
import {
  AnswerEditor,
  defaultBatteryConfig,
  defaultChoicesFor,
  defaultMultiSelectConfig,
  defaultPhotoConfig,
  defaultSliderConfig,
  defaultTextConfig,
  type JumpTargetOption,
} from './AnswerEditors';
import { groupQuestionsForRender } from '../utils/question-layout';
import { ScoringSettingsSection } from './ScoringSettingsSection';

// Enum → i18n key maps. The label rendered in the option is resolved via
// `t()` at render time so the dropdown follows the active locale.
const TARGET_OPTION_KEYS: { value: Target; key: string }[] = [
  { value: 'PATIENT', key: 'enums.target.patient' },
  { value: 'CAREGIVER', key: 'enums.target.caregiver' },
];
const GENDER_OPTION_KEYS: { value: Gender; key: string }[] = [
  { value: 'COMMON', key: 'enums.gender.common' },
  { value: 'MALE_ONLY', key: 'enums.gender.maleOnly' },
  { value: 'FEMALE_ONLY', key: 'enums.gender.femaleOnly' },
];
const APPLICABILITY_OPTION_KEYS: { value: Applicability; key: string }[] = [
  { value: 'COMMON', key: 'enums.applicability.common' },
  { value: 'DISEASE_GROUP', key: 'enums.applicability.group' },
  { value: 'DISEASE_SPECIFIC', key: 'enums.applicability.specific' },
];
const ANSWER_TYPE_OPTION_KEYS: { value: AnswerType; key: string }[] = [
  { value: 'SHORT_ANSWER', key: 'enums.answerType.shortAnswer' },
  { value: 'MULTI_CHOICE', key: 'enums.answerType.multiChoice' },
  { value: 'YES_NO', key: 'enums.answerType.yesNo' },
  { value: 'OX', key: 'enums.answerType.ox' },
  { value: 'TEXT', key: 'enums.answerType.text' },
  { value: 'SLIDER', key: 'enums.answerType.slider' },
  { value: 'PHOTO', key: 'enums.answerType.photo' },
  { value: 'BATTERY', key: 'enums.answerType.battery' },
];

const DEFAULT_ADVANCED: ScoringAdvanced = {
  scoreContribution: true,
  weight: 1.0,
  reliability: 1.0,
  min: 0,
  max: 10,
};

function buildInitialDraft(args: {
  itemId: string;
  defaultTarget: Target | null;
  parentQid: string | null;
  existing: Question | null;
  // For add-sub: the parent question, so the sub-question inherits its
  // Target / Gender / Applied-Area by default (SHCT-243 BR-03).
  parent: Question | null;
}): QuestionDraft {
  if (args.existing) {
    return {
      itemId: args.existing.itemId,
      parentQid: args.existing.parentQid,
      text: args.existing.text,
      target: args.existing.target,
      gender: args.existing.gender,
      applicability: args.existing.applicability,
      diseaseGroupId: args.existing.diseaseGroupId ?? null,
      diseaseCodeId: args.existing.diseaseCodeId ?? null,
      answerType: args.existing.answerType,
      allowPhoto: args.existing.allowPhoto,
      choices: args.existing.choices,
      multiConfig: args.existing.multiConfig,
      textConfig: args.existing.textConfig,
      sliderConfig: args.existing.sliderConfig,
      photoConfig: args.existing.photoConfig,
      batteryConfig: args.existing.batteryConfig,
      advanced: { ...args.existing.advanced },
    };
  }
  // SHCT-438 — a sub-question does NOT inherit its parent's meta. [ADM.048] answered
  // "상속 없이 독립 설정", and [ADM.060] Q2 (Option B) settled how that splits across the
  // three fields: `target` comes from the calling context, `gender` and 적용 범위 start
  // **empty** for the operator to choose. Inheriting them (SHCT-243 BR-03, written before
  // either answer) silently made a 남성 전용 parent produce 남성 전용 children, which is the
  // opposite of what the customer asked for, and the operator had no cue that a value had
  // been chosen for them.
  //
  // `target` still follows the parent rather than `defaultTarget`: it is the calling context
  // here, and it is what keeps the sub-question in the same column as its parent.
  return {
    itemId: args.itemId,
    parentQid: args.parentQid,
    text: '',
    target: args.parent?.target ?? args.defaultTarget ?? 'PATIENT',
    // Empty until chosen — same signal the answer-type select uses below. Only for a
    // sub-question: a plain new main question keeps its existing `공통` defaults, which no
    // clarification has touched.
    gender: args.parent ? ('' as Gender) : 'COMMON',
    applicability: args.parent ? ('' as Applicability) : 'COMMON',
    diseaseGroupId: null,
    diseaseCodeId: null,
    // Empty string signals "no answer type picked" — the select renders the
    // placeholder row and the answer editor stays hidden.
    answerType: '' as AnswerType,
    allowPhoto: false,
    advanced: { ...DEFAULT_ADVANCED },
  };
}

interface QuestionEditPanelProps {
  selectedItemId: string;
}

export function QuestionEditPanel({ selectedItemId }: QuestionEditPanelProps) {
  const { t } = useTranslation('questionnaire-design');
  const editPanel = useQuestionnaireDesignPageStore((s) => s.editPanel);
  const close = useQuestionnaireDesignPageStore((s) => s.closeEditPanel);
  const addSurveyDirty = useDeployDirtyStore((s) => s.addSurvey);

  const { data: questions } = useItemQuestionsQuery(selectedItemId);
  const { data: diseaseOptions } = useDiseaseFilterOptionsQuery();

  // SHCT-517 / [ADM.046] — the Scope the question inherits from its PRO item, shown read-only.
  const { scope: inheritedScope, isUnavailable: scopeUnavailable } =
    useInheritedItemScope(selectedItemId);

  const createMutation = useCreateQuestionMutation();
  const updateMutation = useUpdateQuestionMutation();

  const existing = useMemo(() => {
    if (editPanel.mode !== 'edit' || !editPanel.qid) return null;
    return questions?.find((q) => q.qid === editPanel.qid) ?? null;
  }, [editPanel.mode, editPanel.qid, questions]);

  /**
   * The Scope actually in force for this question (SHCT-517).
   *
   * The item is the owner, so its value wins whenever we have it. The question's own stored
   * `applicability` is the fallback for the window before the server ships the tree field — dropping
   * to a blank there would hide a value the row genuinely still carries.
   */
  const effectiveScope = (inheritedScope?.scope ??
    existing?.applicability ??
    'COMMON') as Applicability;
  const effectiveGroupId =
    inheritedScope?.diseaseGroupId ?? existing?.diseaseGroupId ?? null;
  const effectiveCodeId =
    inheritedScope?.diseaseCodeId ?? existing?.diseaseCodeId ?? null;

  /** Read-only labels for the two cells above — resolved through the disease master. */
  const inheritedScopeLabel = useMemo(() => {
    const scopeKey = APPLICABILITY_OPTION_KEYS.find(
      (o) => o.value === effectiveScope,
    )?.key;
    const scopeLabel = scopeKey ? t(scopeKey) : '-';

    let disease = '';
    if (effectiveScope === 'DISEASE_GROUP' && effectiveGroupId != null) {
      disease =
        (diseaseOptions ?? []).find(({ group }) => group.id === effectiveGroupId)
          ?.group.name ?? '';
    } else if (effectiveScope === 'DISEASE_SPECIFIC' && effectiveCodeId != null) {
      const code = (diseaseOptions ?? [])
        .flatMap(({ codes }) => codes)
        .find((c) => c.id === effectiveCodeId);
      disease = code ? `${code.code} ${code.name}` : '';
    }
    return { scope: scopeLabel, disease };
  }, [effectiveScope, effectiveGroupId, effectiveCodeId, diseaseOptions, t]);

  // Parent question for an add-sub, so the new sub-question can inherit its
  // Target / Gender / Applied-Area defaults (SHCT-243 BR-03).
  const parentQuestion = useMemo(() => {
    if (editPanel.mode !== 'add-sub' || !editPanel.parentQid) return null;
    return questions?.find((q) => q.qid === editPanel.parentQid) ?? null;
  }, [editPanel.mode, editPanel.parentQid, questions]);

  // Eligible JUMP targets. Two groups, matching the prototype's buildBranchOpts:
  //   1. Forward same-bucket siblings — questions after the draft's own order.
  //      For a new question (no qid yet) the draft is appended at the end of its
  //      bucket, so there are no forward siblings (§8.2 cycle rule).
  //   2. A main question's OWN sub-questions (SHCT-435). A parent's answer option
  //      branches into one of its sub-questions (§3.7 "하위문항은 상위 문항의
  //      분기 조건에 연결됨"); the prototype offers `currentQ.children` here. These
  //      live in a different (parentQid) bucket so they aren't siblings, but they
  //      always render after the parent → structurally forward, no cycle (a sub
  //      can only jump to a sibling sub, never back up to the parent).
  const jumpTargets = useMemo<JumpTargetOption[]>(() => {
    if (!questions) return [];
    const siblings = questions.filter(
      (q) =>
        q.target === (existing?.target ?? null) &&
        (q.parentQid ?? null) === (existing?.parentQid ?? null),
    );
    const myOrder = existing?.order ?? siblings.length;
    const tree = groupQuestionsForRender(questions, existing?.target ?? 'PATIENT');
    const numByQid = new Map<string, string>();
    for (const node of tree) {
      numByQid.set(node.question.qid, node.displayNum);
      for (const sub of node.children) {
        numByQid.set(sub.question.qid, sub.displayNum);
      }
    }
    const forwardSiblings = siblings
      .filter((q) => q.qid !== existing?.qid && q.order > myOrder)
      .sort((a, b) => a.order - b.order)
      .map((q) => ({ qid: q.qid, label: numByQid.get(q.qid) ?? '?' }));
    // Own sub-questions apply only when editing an existing main question — a
    // top-level question (parentQid == null) whose children were already saved.
    const ownSubQuestions =
      existing && existing.parentQid == null
        ? questions
            .filter(
              (q) =>
                q.target === existing.target && q.parentQid === existing.qid,
            )
            .sort((a, b) => a.order - b.order)
            .map((q) => ({ qid: q.qid, label: numByQid.get(q.qid) ?? '?' }))
        : [];
    return [...forwardSiblings, ...ownSubQuestions];
  }, [existing, questions]);

  const [draft, setDraft] = useState<QuestionDraft>(() =>
    buildInitialDraft({
      itemId: selectedItemId,
      defaultTarget: editPanel.defaultTarget,
      parentQid: editPanel.parentQid,
      existing,
      parent: parentQuestion,
    }),
  );
  // Stuff the local "what should this slide-panel show?" notice modal here.
  // The page-level component reads it for the post-save deploy notice.
  const [postSaveOpen, setPostSaveOpen] = useState(false);
  // Validation surfaces inline under each field per the §7.1 error standard
  // (Validation Error → inline red text, not a toast/bottom-right block).
  // `submitted` gates display until the first save attempt; the errors
  // themselves are recomputed live so each message clears as its field is fixed.
  const [submitted, setSubmitted] = useState(false);

  // Reset the draft when the panel transitions from closed→open, or when the
  // user re-opens with a different qid.
  useEffect(() => {
    if (!editPanel.open) return;
    setSubmitted(false);
    setDraft(
      buildInitialDraft({
        itemId: selectedItemId,
        defaultTarget: editPanel.defaultTarget,
        parentQid: editPanel.parentQid,
        existing,
        parent: parentQuestion,
      }),
    );
  }, [
    editPanel.open,
    editPanel.qid,
    editPanel.defaultTarget,
    editPanel.parentQid,
    existing,
    parentQuestion,
    selectedItemId,
  ]);

  const editingQid = editPanel.mode === 'edit' ? editPanel.qid : null;
  // Every failing field at once, recomputed live so messages clear as fixed.
  // Only shown after the first save attempt (`submitted`).
  const liveErrors = useMemo(
    () => collectFieldErrors(draft, editingQid, questions ?? []),
    [draft, editingQid, questions],
  );
  const errors: QuestionFieldErrors = submitted ? liveErrors : {};
  const errorText = (key: keyof QuestionFieldErrors) =>
    errors[key] ? t(`validation.${errors[key]}`) : null;
  const saveDisabled = createMutation.isPending || updateMutation.isPending;
  // SHCT-851 — highlight the individual out-of-band score inputs. Gated on
  // `submitted` like every other error here, so a half-typed score is not
  // red-ringed while the operator is still filling the form in.
  const scoreOutOfBand = useMemo(
    () => (submitted ? makeScoreOutOfBand(draft.advanced) : () => false),
    [submitted, draft.advanced],
  );

  const onChangeAnswerType = (next: AnswerType) => {
    // Switching type seeds the new config block with its defaults so the
    // editor never renders against `undefined`. We preserve the previously
    // selected configs so flipping back doesn't drop the work.
    setDraft((prev) => ({
      ...prev,
      answerType: next,
      choices: defaultChoicesFor(next) ?? prev.choices,
      multiConfig:
        next === 'MULTI_CHOICE'
          ? prev.multiConfig ?? defaultMultiSelectConfig()
          : prev.multiConfig,
      textConfig: next === 'TEXT' ? prev.textConfig ?? defaultTextConfig() : prev.textConfig,
      sliderConfig:
        next === 'SLIDER' ? prev.sliderConfig ?? defaultSliderConfig() : prev.sliderConfig,
      photoConfig:
        next === 'PHOTO' ? prev.photoConfig ?? defaultPhotoConfig() : prev.photoConfig,
      batteryConfig:
        next === 'BATTERY'
          ? prev.batteryConfig ?? defaultBatteryConfig()
          : prev.batteryConfig,
    }));
  };

  const handleSave = async () => {
    setSubmitted(true);
    // §7.1 — every failing field renders inline (via `errors`); block the save.
    // Covers required, range/config, and §8.2 branch-cycle checks in one pass.
    if (Object.keys(collectFieldErrors(draft, editingQid, questions ?? [])).length > 0) {
      return;
    }
    // SHCT-517 / [ADM.046] — the question does not own its Scope, so the payload carries the
    // item's values rather than whatever the draft happened to hold. The API contract still
    // requires the three fields, so they are sent inherited instead of dropped; that also
    // repairs rows saved before this change, whose stored Scope may differ from their item's.
    const scoped: QuestionDraft = {
      ...draft,
      applicability: effectiveScope,
      diseaseGroupId: effectiveGroupId,
      diseaseCodeId: effectiveCodeId,
    };
    try {
      let saved: Question;
      if (editPanel.mode === 'edit' && editPanel.qid) {
        saved = await updateMutation.mutateAsync({
          qid: editPanel.qid,
          input: draftToUpdateInput(scoped),
        });
        toast.success(t('editPanel.toasts.updated'));
      } else {
        saved = await createMutation.mutateAsync({
          ...scoped,
          // Ensure the BE receives the right slice for the active answer
          // type (drop any stale subconfigs the user toggled past).
          ...draftToUpdateInput(scoped),
        });
        toast.success(t('editPanel.toasts.created'));
      }
      addSurveyDirty(saved.qid);
      close();
      setPostSaveOpen(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('editPanel.toasts.saveFailed');
      toast.error(msg);
    }
  };

  const title =
    editPanel.mode === 'edit'
      ? draft.parentQid
        ? t('editPanel.titleEditSub')
        : t('editPanel.titleEdit')
      : editPanel.mode === 'add-sub'
        ? t('editPanel.titleAddSub')
        : t('editPanel.titleCreate');

  return (
    <>
      <SlidePanel
        open={editPanel.open}
        title={title}
        width={560}
        onClose={close}
      >
        {/* FEATURE_024 (Survey Design) — `noValidate` turns OFF the browser's own built-in input
            checking so this panel's React validation is the only one that runs. Without it the
            browser checks first: the answer-type config inputs carry real `max` limits (see
            AnswerEditors), so typing an over-limit value made the browser stop the save on its own,
            before this form's own handler could run. The panel's validator therefore never ran, and
            instead of the wording the requirement document fixes for each rule, the operator saw the
            browser's built-in bubble — which is written in the BROWSER's language, i.e. English on a
            Korean-only product. Same cause as the earlier fix that removed one offending `min` from
            the scoring panel, which only cured that single field. */}
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!saveDisabled) handleSave();
          }}
        >
          {/* 1. Target */}
          <div className="form-field" style={{ marginBottom: 'var(--space-4)' }}>
            <label className="form-field__label">
              {t('editPanel.fields.target')}
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {TARGET_OPTION_KEYS.map((opt) => (
                <RadioPill
                  key={opt.value}
                  name="seEditTarget"
                  value={opt.value}
                  label={t(opt.key)}
                  checked={draft.target === opt.value}
                  onChange={() => setDraft({ ...draft, target: opt.value })}
                />
              ))}
            </div>
          </div>

          {/* 2. Gender */}
          <div className="form-field" style={{ marginBottom: 'var(--space-4)' }}>
            <label className="form-field__label">
              {t('editPanel.fields.gender')}
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {GENDER_OPTION_KEYS.map((opt) => (
                <RadioPill
                  key={opt.value}
                  name="seEditGender"
                  value={opt.value}
                  label={t(opt.key)}
                  checked={draft.gender === opt.value}
                  onChange={() => setDraft({ ...draft, gender: opt.value })}
                />
              ))}
            </div>
            {errorText('gender') ? (
              <p className="form-field__error">{errorText('gender')}</p>
            ) : null}
          </div>

          {/* 3. 적용 범위 + 질병군 / 코드 */}
          {/* SHCT-517 / [ADM.046] — 적용 범위는 문항이 갖는 값이 아니라 **소속 항목의 Scope**다.
              편집 컨트롤(적용범위 select · 질병군 select · 질병코드 select)을 읽기 전용 표시로 교체한다.
              소유자는 PRD 04 항목 폼 하나뿐이며, 항목 Scope가 바뀌면 이 표시는 다음 조회에서 그대로
              따라간다 — 문항 단위 동기화 작업은 없다.
              값 출처는 `GET /admin/pro-items/items/{id}`다. 문진 트리 payload에 Scope가 없어서인데,
              그 필드가 들어오면 이 훅만 갈아끼우면 된다(BE 인계 노트). */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--space-4)',
              marginBottom: 'var(--space-4)',
            }}
          >
            <div className="form-field">
              <label className="form-field__label">
                {t('editPanel.fields.applicability')}
              </label>
              <p style={{ margin: 0, padding: '8px 0', fontWeight: 500 }}>
                {inheritedScopeLabel.scope}
              </p>
              {/* The hint claims the value came from the item, so only show it when it did.
                  `scopeUnavailable` means the tree row had no numeric id to resolve — an older
                  server build — and what is on screen is then the question's own stored value. */}
              {!scopeUnavailable ? (
                <p
                  style={{
                    fontSize: 12,
                    color: 'var(--muted-foreground)',
                    margin: '2px 0 0',
                  }}
                >
                  {t('editPanel.fields.applicabilityInherited')}
                </p>
              ) : null}
            </div>
            {inheritedScopeLabel.disease ? (
              <div className="form-field">
                <label className="form-field__label">
                  {t(
                    effectiveScope === 'DISEASE_GROUP'
                      ? 'editPanel.fields.diseaseGroup'
                      : 'editPanel.fields.diseaseCode',
                  )}
                </label>
                <p style={{ margin: 0, padding: '8px 0', fontWeight: 500 }}>
                  {inheritedScopeLabel.disease}
                </p>
              </div>
            ) : null}
          </div>

          {/* Scope guidance */}
          <div
            style={{
              marginBottom: 'var(--space-4)',
              padding: '8px 12px',
              background: '#f0f7ff',
              borderRadius: 'var(--radius-sm)',
              fontSize: 11,
              color: 'var(--muted-foreground)',
              lineHeight: 1.6,
            }}
            dangerouslySetInnerHTML={{
              __html: t('editPanel.fields.scopeGuideHtml'),
            }}
          />

          {/* 4. Question text */}
          <div className="form-field" style={{ marginBottom: 'var(--space-4)' }}>
            <label className="form-field__label">
              {t('editPanel.fields.text')}{' '}
              <span className="required">*</span>
            </label>
            <textarea
              className={`form-field__textarea${errorText('text') ? ' form-field__input--error' : ''}`}
              rows={3}
              value={draft.text}
              aria-invalid={errorText('text') ? 'true' : undefined}
              onChange={(e) => setDraft({ ...draft, text: e.target.value })}
              placeholder={t('editPanel.fields.textPlaceholder')}
            />
            {errorText('text') ? (
              <p className="form-field__error">{errorText('text')}</p>
            ) : null}
          </div>

          {/* 5. Answer type */}
          <div className="form-field" style={{ marginBottom: 'var(--space-4)' }}>
            <label className="form-field__label">
              {t('editPanel.fields.answerType')}{' '}
              <span className="required">*</span>
            </label>
            <select
              className={`form-field__select${errorText('answerType') ? ' form-field__input--error' : ''}`}
              value={draft.answerType || ''}
              aria-invalid={errorText('answerType') ? 'true' : undefined}
              onChange={(e) => onChangeAnswerType(e.target.value as AnswerType)}
            >
              <option value="" disabled>
                {t('editPanel.fields.answerTypePlaceholder')}
              </option>
              {ANSWER_TYPE_OPTION_KEYS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.key)}
                </option>
              ))}
            </select>
            {errorText('answerType') ? (
              <p className="form-field__error">{errorText('answerType')}</p>
            ) : null}
          </div>

          {/* 6. Dynamic answer area */}
          {draft.answerType ? (
            <div className="form-field" style={{ marginBottom: 'var(--space-4)' }}>
              <label className="form-field__label">
                {t('editPanel.fields.answerArea')}
              </label>
              <div
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  background: 'var(--background)',
                }}
              >
                <AnswerEditor
                  answerType={draft.answerType}
                  choices={draft.choices}
                  setChoices={(next) => setDraft({ ...draft, choices: next })}
                  multiConfig={draft.multiConfig}
                  setMultiConfig={(next) => setDraft({ ...draft, multiConfig: next })}
                  textConfig={draft.textConfig}
                  setTextConfig={(next) => setDraft({ ...draft, textConfig: next })}
                  sliderConfig={draft.sliderConfig}
                  setSliderConfig={(next) => setDraft({ ...draft, sliderConfig: next })}
                  photoConfig={draft.photoConfig}
                  setPhotoConfig={(next) => setDraft({ ...draft, photoConfig: next })}
                  batteryConfig={draft.batteryConfig}
                  setBatteryConfig={(next) => setDraft({ ...draft, batteryConfig: next })}
                  jumpTargets={jumpTargets}
                  scoreOutOfBand={scoreOutOfBand}
                />
              </div>
              {errorText('answerArea') ? (
                <p className="form-field__error">{errorText('answerArea')}</p>
              ) : null}
            </div>
          ) : (
            <div className="form-field" style={{ marginBottom: 'var(--space-4)' }}>
              <label className="form-field__label">
                {t('editPanel.fields.answerArea')}
              </label>
              <div
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  background: 'var(--background)',
                  minHeight: 60,
                  textAlign: 'center',
                  color: 'var(--muted-foreground)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {t('editPanel.fields.answerAreaPrompt')}
              </div>
            </div>
          )}

          {/* 7. Photo toggle */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              marginTop: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
              padding: 'var(--space-3) var(--space-4)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--card)',
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <input
                type="checkbox"
                checked={draft.allowPhoto}
                onChange={(e) => setDraft({ ...draft, allowPhoto: e.target.checked })}
                style={{ width: 16, height: 16, accentColor: 'var(--info)' }}
              />
              {t('editPanel.fields.allowPhoto')}
            </label>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--muted-foreground)' }}>
              {t('editPanel.fields.allowPhotoHint')}
            </span>
          </div>

          {/* 8. 점수 산출 설정 */}
          <ScoringSettingsSection
            advanced={draft.advanced}
            setAdvanced={(next) => setDraft({ ...draft, advanced: next })}
            weightError={errorText('weight')}
          />

          {/* 9. Save / Cancel */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--space-2)',
              marginTop: 'var(--space-6)',
            }}
          >
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={close}
            >
              {t('editPanel.actions.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-default btn-primary"
              disabled={saveDisabled}
              data-perm="PRO_MGMT:WRITE"
            >
              {t('editPanel.actions.save')}
            </button>
          </div>
        </form>
      </SlidePanel>

      <PostSaveDeployNotice
        open={postSaveOpen}
        action="saved"
        onClose={() => setPostSaveOpen(false)}
      />
    </>
  );
}

// Lightweight radio button styled like the prototype's `.se-radio-label`.
// Kept inside the panel because no other admin-web page uses this exact look.
function RadioPill({
  name,
  value,
  label,
  checked,
  onChange,
}: {
  name: string;
  value: string;
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 'var(--font-size-sm)',
        cursor: 'pointer',
        padding: '5px 12px',
        border: `1.5px solid ${checked ? 'var(--info)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-sm)',
        background: checked ? 'var(--badge-primary-bg)' : 'transparent',
        color: checked ? 'var(--badge-primary-text)' : undefined,
        transition: 'all 0.15s',
        userSelect: 'none',
      }}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        style={{ display: 'none' }}
      />
      <span>{label}</span>
    </label>
  );
}

// SHCT-603 — `draftToPreviewQuestion` is gone. It existed only to feed the live
// normalized/final read-outs in ScoringSettingsSection, which ADM.040 removed.
